import React, { useEffect, useRef, useState } from 'react'
import { Box } from '@mui/material'
import { useForm } from 'react-hook-form'

import styles from './AccountSettings.module.scss'
import { LxLoadingSpinner } from '@components/loader/loadingSpinner.tsx'
import LxCancelButton from '@src/components/cancelButton/LxCancelButton'
import LxSaveButton from '@src/components/saveButton/LxSaveButton'
import { useDataProvider, useNotify } from 'react-admin'
import { AccountSettings } from '@src/logic/contexts/Accounts/AccountsFrontend.type'
import { AccountSettingField } from './AccountSettingField'
import { SettingsIcon } from '@src/assets/icons/utils'
import { LxIcon } from '@src/components/icon/Icon'
import { useUserContext } from '@src/logic/contexts/AppStore/UserContext'
import { useConfirmNavigation } from '@src/hooks/useConfirmNavigation'
import { useModal } from '@logic/contexts/Modal/ModalContext.tsx'
import modalStyles from '@components/modal/modal.module.scss'
import {  useNavigate } from 'react-router-dom'

type AccountSettingKey = {
  user_scope: string[]
  app_scope: string[]
  description: string
  enforced_by: string
  effective_value: string
  ui: {
    visibility: string
    label: string
    description: string
    element: string
  }
  values: {
    vendor: string | null
    district: string | null
    school: string | null
  }
}

function getDefaultValue(setting: any) {
  const effectiveValue = setting.effective_value
  const elementType = setting.ui?.element?.toLowerCase()

  if (effectiveValue === undefined || effectiveValue === null) {
    return elementType === 'boolean' ? false : ''
  }

  // Handle different field types
  switch (elementType) {
    case 'boolean':
      // Convert string values to boolean
      if (typeof effectiveValue === 'string') {
        return effectiveValue === 'true'
      }
      return Boolean(effectiveValue)
    case 'number':
      // Ensure number fields get numeric values
      return effectiveValue ? Number(effectiveValue) : 0
    default:
      return effectiveValue
  }
}

type EditableSettings = Record<string, AccountSettingKey>

export const AccountSettingsForm = () => {
  // const { accountId } = useParams()
  const { currentUser, isUserLoading } = useUserContext() as any
  const accountId = currentUser?.accountId || ''

  const dataProvider = useDataProvider()
  const notify = useNotify()
  const navigate = useNavigate()
  const { showModal, hideModal } = useModal()

  const [isFormSubmitting, setIsFormSubmitting] = useState(false)
  const [isSettingDataLoading, setIsSettingDataLoading] = useState(true)
  const [settings, setSettings] = useState<EditableSettings | null>(null)
  const [editorLevel, setEditorLevel] = useState<'school' | 'district' | 'vendor'>('school') // based on the level we will enable/disable fields according to enforcement
  const [enforceMap, setEnforceMap] = useState<Record<string, boolean>>({})
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false)

  // retrive account settings

  const fetchData = async () => {
    if (!accountId || isUserLoading) return
    setIsSettingDataLoading(true)
    try {
      const response = await dataProvider.getOne('Account', {
        id: accountId,
        method: 'accountSettings',
      })

      if (response.data) {
        const settingsData = response.data as AccountSettings
        const excludeKeys: (keyof AccountSettings)[] = ['id', 'account_id', 'type', 'description', 'requester_level']
        const editableSettings: Partial<AccountSettings> = {}

        Object.entries(settingsData).forEach(([key, value]) => {
          if (excludeKeys.includes(key as keyof AccountSettings)) return
          if (value && value.ui && value.ui.visibility === 'editable') {
            editableSettings[key as keyof AccountSettings] = value
          }
        })
        setEditorLevel(settingsData?.requester_level || 'school')
        setSettings(editableSettings)
      } else {
        setSettings({})
      }
    } catch (error) {
      setSettings({})
    } finally {
      setIsSettingDataLoading(false)
    }
  }

  useEffect(() => {
    fetchData()
  }, [dataProvider, accountId, isUserLoading])

  const defaultValues = React.useMemo(() => {
    if (!settings) return {}
    return Object.fromEntries(Object.entries(settings).map(([key, setting]) => [key, getDefaultValue(setting)]))
  }, [settings])

  const {
    handleSubmit,
    control,
    reset,
    formState: { isDirty },
  } = useForm({
    defaultValues,
  })

  // Check for changes whenever form isDirty or enforcement map changes
  useEffect(() => {
    const enforcementHasChanges = Object.keys(enforceMap).length > 0
    setHasUnsavedChanges(isDirty || enforcementHasChanges)
  }, [isDirty, enforceMap])

  const confirmMsg = 'You have unsaved changes that will be discarded. Do you want to proceed?'
  const { confirmNavigation, confirm, cancel, message } = useConfirmNavigation(hasUnsavedChanges, confirmMsg)

  useEffect(() => {
    const beforeUnload = (e: BeforeUnloadEvent) => {
      if (!hasUnsavedChanges) return
      e.preventDefault()
      e.returnValue = ''
    }
    window.addEventListener('beforeunload', beforeUnload)
    return () => window.removeEventListener('beforeunload', beforeUnload)
  }, [hasUnsavedChanges])

  useEffect(() => {
    reset(defaultValues)
  }, [defaultValues, reset])

  const onSubmit = async (formData: any) => {
    if (!accountId || !settings) return
    setIsFormSubmitting(true)

    try {
      const payload: Record<string, any> = {}

      Object.entries(formData).forEach(([key, value]) => {
        const setting = settings[key]
        const canEnforce = false // for now there is no enforce slider will be shown, all the settings are enforced to teh schools in the backend,
        // const canEnforce = setting && ['vendor', 'district'].includes(editorLevel) // vendor or district only
        const hasUserToggled = key in enforceMap

        if (!setting) return

        if (canEnforce) {
          if (hasUserToggled) {
            const enforced_by = enforceMap[key] ? editorLevel : null
            payload[key] = { value, enforced_by }
          } else {
            payload[key] = value // no enforcement change
          }
        } else {
          // school — always send value directly
          payload[key] = value
        }
      })

      await dataProvider.update('Account', {
        id: accountId,
        data: { data: payload, action: 'update_account_settings' },
        previousData: null,
      })
      notify('Settings were updated successfully. New settings will apply the next time users sign in.', { type: 'success' })
      setEnforceMap({})
      setHasUnsavedChanges(false)
      await fetchData()
    } catch (error) {
      notify('Failed to update account settings', { type: 'error' })
    } finally {
      setIsFormSubmitting(false)
    }
  }

  const modalRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    // Focus the modal when opens
    modalRef.current?.focus()
  }, [])

  const showCancelConfirmation = () => {
    const modalContent = (
      <div role='dialog' aria-label='Cancel confirmation' tabIndex={-1} ref={modalRef}>
        <div className={modalStyles.dialogHeader}>
          <h3>Confirmation Required</h3>
        </div>
        <div className={modalStyles.dialogContent}>
          <p>You have unsaved changes that will be discarded. Do you want to proceed?</p>
        </div>
        <div className={modalStyles.dialogActions}>
          <LxCancelButton label='Cancel' classNames='lxActionButton,lxActionButtonDefaultSize' onCancel={hideModal} />
          <LxSaveButton
            label='Reset'
            classNames='lxActionButton,lxActionButtonFilled,lxActionButtonDefaultSize'
            onSubmit={() => {
              hideModal()
              reset(defaultValues)
              setEnforceMap({})
              setHasUnsavedChanges(false)
            }}
          />
        </div>
      </div>
    )
    showModal(modalContent)
  }

  const showNavigationConfirmation = () => {
    const modalContent = (
      <div role='dialog' aria-label='Cancel confirmation' tabIndex={-1} ref={modalRef}>
        <div className={modalStyles.dialogHeader}>
          <h3>Confirmation Required</h3>
        </div>
        <div className={modalStyles.dialogContent}>
          <p>{message || confirmMsg}</p>
        </div>
        <div className={modalStyles.dialogActions}>
          <LxCancelButton
            label='Stay'
            classNames='lxActionButton,lxActionButtonDefaultSize'
            onCancel={() => {
              hideModal()
              cancel()
            }}
          />
          <LxSaveButton
            label='Leave'
            classNames='lxActionButton,lxActionButtonFilled,lxActionButtonDefaultSize'
            onSubmit={() => {
              hideModal()
              confirm()
            }}
          />
        </div>
      </div>
    )
    showModal(modalContent)
  }

  const handleCancel = () => {
    if (hasUnsavedChanges) {
      showCancelConfirmation()
    } else {
      reset(defaultValues)
      setEnforceMap({})
      setHasUnsavedChanges(false)
      navigate('/')
    }
  }

  const handleEnforcementChange = (settingKey: string, enforced: boolean) => {
    setEnforceMap((m) => ({ ...m, [settingKey]: enforced }))
  }

  // Show navigation confirmation modal when needed
  useEffect(() => {
    if (confirmNavigation) {
      showNavigationConfirmation()
    }
  }, [confirmNavigation])

  const isLoading = isSettingDataLoading || isFormSubmitting

  return (
    <div>
      <div>
        <div class={styles.accountSettingsHeader}>
          <LxIcon icon={SettingsIcon} sxStyles={{ height: '80px', width: '80px' }}></LxIcon>
          <div>
            <h4>Global Settings</h4>
            {editorLevel && editorLevel !== 'school' && <span>These settings will apply <u>across all accounts</u> under your administration</span>}
          </div>
        </div>
      </div>

      <form onSubmit={handleSubmit(onSubmit)} className={styles.AccountSettingsForm} role='form'>
        {isLoading && (
          <div className={styles.LoadingOverlay}>
            <LxLoadingSpinner />
          </div>
        )}

        {!isLoading && (!settings || Object.keys(settings).length === 0) ? (
          <div className={styles.noSettingsMsg}>No editable settings are available for this account.</div>
        ) : (
          <>
            <Box className={styles.settingsGrid}>
              {Object.entries(settings ?? {}).map(([key, setting]) => (
                <div key={key} className={styles.settingItem}>
                  <AccountSettingField settingKey={key} setting={setting} control={control} editorLevel={editorLevel} onEnforceChange={handleEnforcementChange} isEnforced={enforceMap[key]} />
                </div>
              ))}
            </Box>

            <div className={styles.formActions}>
              <LxCancelButton label='Cancel' classNames='lxActionButton,lxActionButtonDefaultSize' onCancel={handleCancel} />
              <LxSaveButton label='Update' classNames='lxActionButton,lxActionButtonFilled,lxActionButtonDefaultSize' onSubmit={handleSubmit(onSubmit)} disabled={!hasUnsavedChanges} />
            </div>
          </>
        )}
      </form>
    </div>
  )
}

export default AccountSettingsForm
